import { useCallback } from "react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";
import { formatISO } from "date-fns";
import { withReversedByPeriodId } from "@common/helpers/domains/accounting/scheduleLadderPeriods";
import { summarizeSchedulePeriods } from "@common/helpers/domains/accounting/schedulePeriodsSummary";
import { scheduleContract } from "@common/contracts/schedule.contract";
import {
    ApplyScheduleTemplateBody,
    ApplyScheduleTemplateBulkBody,
    ApplyScheduleTemplateBulkResponse,
    ApplyScheduleTemplateResponse,
    UnapplyScheduleApplicationsBody,
    UnapplyScheduleApplicationsResponse,
    applyScheduleTemplateResponseSchema,
} from "@common/schemas/domains/accounting/scheduleApplication.schema";
import {
    ListWorkspaceScheduleApplicationsQuery,
    ListWorkspaceScheduleApplicationsResponse,
    ScheduleApplicationListItem,
} from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import { selectAvailableScheduleTemplates } from "@components/accounting/schedules/scheduleTemplateAvailability";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { keepPreviousDataOnPageChange } from "./keepPreviousDataOnPageChange";
import {
    ScheduleLifecycleActionBody,
    ScheduleStopSettlement,
    ScheduleLifecycleChange,
    ScheduleLifecycleProposal,
} from "@common/schemas/domains/accounting/scheduleLifecycle.schema";
import {
    CreateScheduleTemplateBodyInput,
    PreviewScheduleRecognitionBody,
    PreviewScheduleRecognitionResponse,
    ScheduleTemplateListItem,
    scheduleTemplateListItemSchema,
    ScheduleTemplateResponse,
    scheduleTemplateSchema,
} from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import {
    invalidateTransactionsQueries,
    updateTransactionInCache,
} from "@components/transactions/useTransactionsQuery";
import {
    ScheduleApplicationStatus,
    ScheduleChangeJournalEntryMode,
    ScheduleDeleteJournalEntryMode,
    ScheduleLifecycleVerb,
    ScheduleSourceType,
} from "@common/types/domains/accounting/schedule";
import { SCHEDULE_LIFECYCLE_ERROR_TOASTS } from "@common/constants/domains/accounting/schedules";
import { invalidateBillQueries } from "./bill.api";
import { invalidateInvoiceQueries } from "./invoice.api";
import { invalidateTransactionDetailsQueries } from "@components/transactions/TransactionDetails/useTransactionDetailsQuery";
import {
    ScheduleLinkedSourceConflictError,
    SchedulePeriodLockedError,
} from "./scheduleErrors";

import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useScope } from "@state/scopeContext.lib";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { useAllowFeature } from "@hooks/useAllowFeature";
import {
    extractApiErrorMessage,
    getApiErrorMessage,
    getApiErrorStatus,
} from "./apiError";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import {
    getScheduleLifecyclePeriodLockedError,
    getScheduleLinkedSourceConflict,
} from "@helpers/lock";
import { queryClient } from "../queryClient";

export const scheduleTemplateClient = getBackendAPIClient(scheduleContract);

const LIFECYCLE_PROPOSAL_QUERY_SEGMENT = "lifecycle-proposal";

export const scheduleTemplateQueryKeys = {
    all: (workspaceId: string) => ["schedule-templates", workspaceId] as const,
    list: (workspaceId: string, includeArchived = false) =>
        [
            ...scheduleTemplateQueryKeys.all(workspaceId),
            "list",
            includeArchived,
        ] as const,
    detail: (workspaceId: string, id: string) =>
        [...scheduleTemplateQueryKeys.all(workspaceId), "detail", id] as const,
    preview: (
        workspaceId: string,
        body: PreviewScheduleRecognitionBody | null,
    ) =>
        [
            ...scheduleTemplateQueryKeys.all(workspaceId),
            "preview",
            body,
        ] as const,
    applications: (workspaceId: string, templateId: string) =>
        [
            ...scheduleTemplateQueryKeys.all(workspaceId),
            "applications",
            templateId,
        ] as const,
    workspaceApplications: (
        workspaceId: string,
        query: ListWorkspaceScheduleApplicationsQuery,
    ) =>
        [
            ...scheduleTemplateQueryKeys.all(workspaceId),
            "workspace-applications",
            query,
        ] as const,
    lifecycleProposal: (
        workspaceId: string,
        request: ScheduleLifecycleRequest | null,
    ) =>
        [
            ...scheduleTemplateQueryKeys.all(workspaceId),
            LIFECYCLE_PROPOSAL_QUERY_SEGMENT,
            request,
        ] as const,
};

export interface ScheduleLifecycleRequest {
    applicationId: string;
    entityId: number;
    verb: ScheduleLifecycleVerb;
    sourceType?: ScheduleSourceType;
    /** The scheduled amount, shown read-only while the change form collects new terms. */
    amount?: number;
    change?: ScheduleLifecycleChange;
}

export function invalidateScheduleTemplates(workspaceId: string) {
    void queryClient.invalidateQueries({
        queryKey: scheduleTemplateQueryKeys.all(workspaceId),
    });
}

function useHasSchedulesPlan(): boolean {
    const { hasAccess } = useAllowFeature(PREMIUM_FEATURES.SCHEDULES, {
        shouldSkipSupportBypass: true,
    });
    return hasAccess;
}

export function useScheduleTemplates(
    options: {
        enabled?: boolean;
        includeArchived?: boolean;
        /** Lists milestone policies even while their flag is off, for names of existing schedules. */
        includeMilestone?: boolean;
    } = {},
) {
    const { workspaceId } = useScope();
    const hasSchedulesPlan = useHasSchedulesPlan();
    const { schedulesMilestoneBased: isMilestoneEnabled } = useTypedFlags();
    const includeArchived = options.includeArchived ?? false;
    const includeMilestone = options.includeMilestone ?? false;
    const select = useCallback(
        (templates: ScheduleTemplateListItem[]) =>
            selectAvailableScheduleTemplates({
                templates,
                isMilestoneEnabled,
                includeMilestone,
            }),
        [includeMilestone, isMilestoneEnabled],
    );

    return useQuery({
        select,
        queryKey: scheduleTemplateQueryKeys.list(
            workspaceId ?? "",
            includeArchived,
        ),
        enabled:
            workspaceId != null &&
            hasSchedulesPlan &&
            options.enabled !== false,
        queryFn: async (): Promise<ScheduleTemplateListItem[]> => {
            const response = await scheduleTemplateClient.listTemplates({
                params: { workspaceId: workspaceId ?? "" },
                query: includeArchived ? { includeArchived: "true" } : {},
            });
            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to load schedules",
                    ),
                );
            }
            return response.body.templates.map((template) =>
                scheduleTemplateListItemSchema.parse(template),
            );
        },
    });
}

async function loadScheduleTemplateDetail(params: {
    workspaceId: string;
    id: string;
}): Promise<ScheduleTemplateResponse> {
    const response = await scheduleTemplateClient.getTemplate({
        params: { workspaceId: params.workspaceId, id: params.id },
    });
    if (response.status !== 200) {
        throw new Error(
            extractApiErrorMessage(response.body, "Failed to load schedule"),
        );
    }
    return scheduleTemplateSchema.parse(response.body.template);
}

export async function fetchScheduleTemplateDetail(params: {
    workspaceId: string;
    id: string;
}): Promise<ScheduleTemplateResponse> {
    return await queryClient.fetchQuery({
        queryKey: scheduleTemplateQueryKeys.detail(
            params.workspaceId,
            params.id,
        ),
        queryFn: () => loadScheduleTemplateDetail(params),
    });
}

export function useScheduleTemplate(id: string | null) {
    const { workspaceId } = useScope();
    const hasSchedulesPlan = useHasSchedulesPlan();

    return useQuery({
        queryKey: scheduleTemplateQueryKeys.detail(workspaceId ?? "", id ?? ""),
        enabled: workspaceId != null && hasSchedulesPlan && id != null,
        queryFn: async (): Promise<ScheduleTemplateResponse> =>
            await loadScheduleTemplateDetail({
                workspaceId: workspaceId ?? "",
                id: id ?? "",
            }),
    });
}

export function useWorkspaceScheduleApplications(
    query: ListWorkspaceScheduleApplicationsQuery,
    options: { enabled?: boolean } = {},
) {
    const { workspaceId } = useScope();
    const hasSchedulesPlan = useHasSchedulesPlan();
    const queryKey = scheduleTemplateQueryKeys.workspaceApplications(
        workspaceId ?? "",
        query,
    );

    const { data, isLoading, isFetching, isError } = useQuery({
        queryKey,
        enabled:
            workspaceId != null &&
            hasSchedulesPlan &&
            options.enabled !== false,
        placeholderData: keepPreviousDataOnPageChange(queryKey),
        queryFn:
            async (): Promise<ListWorkspaceScheduleApplicationsResponse> => {
                const response =
                    await scheduleTemplateClient.listWorkspaceApplications({
                        params: { workspaceId: workspaceId ?? "" },
                        query,
                    });
                if (response.status !== 200) {
                    throw new Error(
                        extractApiErrorMessage(
                            response.body,
                            "Failed to load schedules",
                        ),
                    );
                }
                return response.body;
            },
    });

    return {
        applications: data?.applications,
        total: data?.total ?? 0,
        pageCount: data?.pageCount ?? 1,
        counterpartyNames: data?.facets.counterpartyNames ?? [],
        lineItemLabels: data?.facets.lineItemLabels ?? [],
        isLoading,
        isFetching,
        isError,
    };
}

async function listApplicationsForTemplate(options: {
    workspaceId: string;
    templateId: string;
}): Promise<ScheduleApplicationListItem[]> {
    const response = await scheduleTemplateClient.listApplications({
        params: { workspaceId: options.workspaceId, id: options.templateId },
    });
    if (response.status !== 200) {
        throw new Error(
            extractApiErrorMessage(
                response.body,
                "Failed to load schedule applications",
            ),
        );
    }
    return response.body.applications;
}

export function useScheduleApplications(templateId: string | null) {
    const { workspaceId } = useScope();
    const hasSchedulesPlan = useHasSchedulesPlan();

    return useQuery({
        queryKey: scheduleTemplateQueryKeys.applications(
            workspaceId ?? "",
            templateId ?? "",
        ),
        enabled: workspaceId != null && hasSchedulesPlan && templateId != null,
        queryFn: () =>
            listApplicationsForTemplate({
                workspaceId: workspaceId ?? "",
                templateId: templateId ?? "",
            }),
    });
}

/**
 * One-off read for flows that need the current state of an application right
 * now — e.g. counting its journal entries before a destructive confirm.
 */
export async function fetchScheduleApplication(options: {
    workspaceId: string;
    templateId: string;
    applicationId: string;
}): Promise<ScheduleApplicationListItem | undefined> {
    const applications = await queryClient.fetchQuery({
        queryKey: scheduleTemplateQueryKeys.applications(
            options.workspaceId,
            options.templateId,
        ),
        queryFn: () => listApplicationsForTemplate(options),
        staleTime: 0,
    });
    return applications.find(
        (application) => application.id === options.applicationId,
    );
}

export function usePreviewScheduleRecognition(
    body: PreviewScheduleRecognitionBody | null,
) {
    const { workspaceId } = useScope();
    const hasSchedulesPlan = useHasSchedulesPlan();

    return useQuery({
        queryKey: scheduleTemplateQueryKeys.preview(workspaceId ?? "", body),
        enabled: workspaceId != null && hasSchedulesPlan && body != null,
        placeholderData: keepPreviousData,
        // A refusal is deterministic for the given ladder; retrying only
        // keeps the previous rows on screen while the message waits.
        retry: false,
        queryFn: async (): Promise<PreviewScheduleRecognitionResponse> => {
            if (body == null) {
                throw new Error("Preview body is required");
            }
            // The preview re-runs on every keystroke, so a refusal (an amount
            // too small to spread) is shown in place rather than toasted.
            const response = await requestScheduleAction(
                () =>
                    scheduleTemplateClient.previewRecognition({
                        extraHeaders: SUPPRESS_ERROR_TOAST_HEADERS,
                        params: { workspaceId: workspaceId ?? "" },
                        body,
                    }),
                "Failed to preview schedule",
            );
            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to preview schedule",
                    ),
                );
            }
            return response.body;
        },
    });
}

export function useCreateScheduleTemplateMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (payload: CreateScheduleTemplateBodyInput) => {
            const response = await scheduleTemplateClient.createTemplate({
                params: { workspaceId: workspaceId ?? "" },
                body: payload,
            });
            if (response.status !== 201) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to create policy",
                    ),
                );
            }
            return scheduleTemplateSchema.parse(response.body.template);
        },
        onSuccess: () => {
            if (workspaceId) {
                invalidateScheduleTemplates(workspaceId);
            }
        },
    });
}

export function useUpdateScheduleTemplateMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (options: {
            id: string;
            payload: CreateScheduleTemplateBodyInput;
        }) => {
            const response = await scheduleTemplateClient.updateTemplate({
                params: { workspaceId: workspaceId ?? "", id: options.id },
                body: options.payload,
            });
            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to update schedule",
                    ),
                );
            }
            return scheduleTemplateSchema.parse(response.body.template);
        },
        onSuccess: () => {
            if (workspaceId) {
                invalidateScheduleTemplates(workspaceId);
            }
        },
    });
}

export const SUPPRESS_ERROR_TOAST_HEADERS = {
    [SUPPRESS_ERROR_TOAST_HEADER]: "true",
};

/**
 * Apply and the lifecycle verbs answer a locked period with a structured 409
 * that has its own dialog, so the api client's default toast is suppressed
 * here. Every other refusal is re-thrown carrying the server's message for
 * the caller to toast.
 */
export async function requestScheduleAction<T>(
    request: () => Promise<T>,
    fallbackMessage: string,
    /** Turns a refusal with its own shape into a typed error, before the generic one. */
    mapError?: (error: AxiosError) => Error | null,
): Promise<T> {
    try {
        return await request();
    } catch (error) {
        const lockedError = getScheduleLifecyclePeriodLockedError(error);
        if (lockedError) {
            throw new SchedulePeriodLockedError(lockedError);
        }
        const linkedConflict = getScheduleLinkedSourceConflict(error);
        if (linkedConflict) {
            throw new ScheduleLinkedSourceConflictError(linkedConflict);
        }
        if (mapError && isAxiosError(error)) {
            const mapped = mapError(error);
            if (mapped) {
                throw mapped;
            }
        }
        throw new Error(getApiErrorMessage(error, fallbackMessage));
    }
}

export function useApplyScheduleTemplateMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (options: {
            templateId: string;
            payload: ApplyScheduleTemplateBody;
        }): Promise<ApplyScheduleTemplateResponse> => {
            const response = await requestScheduleAction(
                () =>
                    scheduleTemplateClient.applyTemplate({
                        extraHeaders: SUPPRESS_ERROR_TOAST_HEADERS,
                        params: {
                            workspaceId: workspaceId ?? "",
                            id: options.templateId,
                        },
                        body: options.payload,
                    }),
                "Failed to apply schedule",
            );
            if (response.status !== 201) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to apply schedule",
                    ),
                );
            }
            return applyScheduleTemplateResponseSchema.parse(response.body);
        },
        onSuccess: (response, options) => {
            if (options.payload.sourceType === ScheduleSourceType.transaction) {
                const scheduleName = workspaceId
                    ? queryClient
                          .getQueryData<ScheduleTemplateListItem[]>(
                              scheduleTemplateQueryKeys.list(workspaceId),
                          )
                          ?.find(
                              (template) => template.id === options.templateId,
                          )?.name
                    : undefined;
                const transactionId = options.payload.transactionId;
                if (scheduleName != null) {
                    updateTransactionInCache(transactionId, (transaction) => ({
                        ...transaction,
                        schedule: {
                            scheduleApplicationId: response.application.id,
                            scheduleTemplateId: options.templateId,
                            ledgerId: response.application.ledgerId,
                            scheduleName,
                            status: ScheduleApplicationStatus.active,
                            ...summarizeSchedulePeriods({
                                amount: response.application.amount,
                                periods: withReversedByPeriodId(
                                    response.periods,
                                ),
                                asOfDate: formatISO(new Date(), {
                                    representation: "date",
                                }),
                            }),
                        },
                    }));
                }
                void invalidateTransactionsQueries();
                void invalidateTransactionDetailsQueries(transactionId);
            }
            if (workspaceId) {
                invalidateScheduleTemplates(workspaceId);
            }
        },
    });
}

export function useApplyScheduleTemplateBulkMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (options: {
            templateId: string;
            payload: ApplyScheduleTemplateBulkBody;
        }): Promise<ApplyScheduleTemplateBulkResponse> => {
            // The caller toasts the server's message, so the api client's
            // own toast is suppressed to avoid showing the refusal twice.
            const response = await requestScheduleAction(
                () =>
                    scheduleTemplateClient.applyTemplateBulk({
                        extraHeaders: SUPPRESS_ERROR_TOAST_HEADERS,
                        params: {
                            workspaceId: workspaceId ?? "",
                            id: options.templateId,
                        },
                        body: options.payload,
                    }),
                "Failed to apply schedule",
            );
            if (response.status !== 201) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to apply schedule",
                    ),
                );
            }
            return response.body;
        },
        onSuccess: () => {
            void invalidateTransactionDetailsQueries();
            if (workspaceId) {
                invalidateScheduleTemplates(workspaceId);
            }
        },
    });
}

export function useUnapplyScheduleApplicationsMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (
            payload: UnapplyScheduleApplicationsBody,
        ): Promise<UnapplyScheduleApplicationsResponse> => {
            const response = await scheduleTemplateClient.unapplyApplications({
                params: { workspaceId: workspaceId ?? "" },
                body: payload,
            });
            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to undo schedule",
                    ),
                );
            }
            return response.body;
        },
        onSuccess: () => {
            void invalidateTransactionDetailsQueries();
            if (workspaceId) {
                invalidateScheduleTemplates(workspaceId);
            }
        },
    });
}

export function useScheduleLifecycleProposal(
    request: ScheduleLifecycleRequest | null,
) {
    const { workspaceId } = useScope();
    const hasSchedulesPlan = useHasSchedulesPlan();

    return useQuery({
        queryKey: scheduleTemplateQueryKeys.lifecycleProposal(
            workspaceId ?? "",
            request,
        ),
        queryFn: async (): Promise<ScheduleLifecycleProposal> => {
            if (!request) {
                throw new Error("Lifecycle proposal request is missing");
            }
            const response = await requestScheduleAction(
                () =>
                    scheduleTemplateClient.getLifecycleProposal({
                        extraHeaders: SUPPRESS_ERROR_TOAST_HEADERS,
                        params: {
                            workspaceId: workspaceId ?? "",
                            id: request.applicationId,
                        },
                        query: {
                            entityId: request.entityId,
                            verb: request.verb,
                            changeAmount: request.change?.amount,
                            changeTermLength: request.change?.termLength,
                            changeTermUnit: request.change?.termUnit,
                            changeTermStart: request.change?.termStart,
                            changeShouldSyncAmountToSource: request.change
                                ?.shouldSyncAmountToSource
                                ? "true"
                                : undefined,
                            changeComponents: request.change?.components
                                ? JSON.stringify(request.change.components)
                                : undefined,
                            changeRecognitionAccountId:
                                request.change?.recognitionAccountId,
                        },
                    }),
                SCHEDULE_LIFECYCLE_ERROR_TOASTS[request.verb],
            );
            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        SCHEDULE_LIFECYCLE_ERROR_TOASTS[request.verb],
                    ),
                );
            }
            return response.body;
        },
        enabled: Boolean(workspaceId && hasSchedulesPlan && request),
        staleTime: 0,
        gcTime: 0,
        retry: false,
    });
}

/**
 * Query roots a lifecycle action invalidates beyond the template tree.
 *
 * Duplicated as literals rather than imported: `scheduleReview.api` and this
 * module already depend on each other, and the schedules report keys live in a
 * third module that would join the cycle. A lifecycle action changes what both
 * of them read, so leaving them out left the report and the review list
 * showing the pre-action ladder until a hard refresh.
 */
const SCHEDULE_REVIEW_QUERY_ROOT = "schedule-review";
const SCHEDULES_REPORT_QUERY_ROOTS = [
    "schedules-report",
    "schedules-report-date-bounds",
];

export function invalidateAfterLifecycleAction(
    workspaceId: string | null | undefined,
) {
    if (workspaceId) {
        // The finished action's own preview stays put: re-asking it about a
        // schedule the action just moved on would only answer with an error.
        void queryClient.invalidateQueries({
            queryKey: scheduleTemplateQueryKeys.all(workspaceId),
            predicate: (query) =>
                !query.queryKey.includes(LIFECYCLE_PROPOSAL_QUERY_SEGMENT),
        });
        void queryClient.invalidateQueries({
            queryKey: [SCHEDULE_REVIEW_QUERY_ROOT, workspaceId],
        });
    }
    for (const root of SCHEDULES_REPORT_QUERY_ROOTS) {
        void queryClient.invalidateQueries({ queryKey: [root] });
    }
    void invalidateTransactionsQueries();
    invalidateInvoiceQueries();
    invalidateBillQueries();
}

// `delete` is absent on purpose: it can retire the application row instead of
// returning it, so it has its own mutation rather than this shared one.
const LIFECYCLE_ACTION_CLIENTS = {
    [ScheduleLifecycleVerb.cancel]: scheduleTemplateClient.cancelApplication,
    [ScheduleLifecycleVerb.pause]: scheduleTemplateClient.pauseApplication,
    [ScheduleLifecycleVerb.resume]: scheduleTemplateClient.resumeApplication,
    [ScheduleLifecycleVerb.change]: scheduleTemplateClient.changeApplication,
} satisfies Partial<
    Record<
        ScheduleLifecycleVerb,
        typeof scheduleTemplateClient.cancelApplication
    >
>;

export type ScheduleLifecycleActionVerb = keyof typeof LIFECYCLE_ACTION_CLIENTS;

export function isScheduleLifecycleActionVerb(
    verb: ScheduleLifecycleVerb,
): verb is ScheduleLifecycleActionVerb {
    return verb in LIFECYCLE_ACTION_CLIENTS;
}

export function useScheduleLifecycleActionMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (
            options: ScheduleLifecycleActionBody & {
                applicationId: string;
                verb: ScheduleLifecycleActionVerb;
                change?: ScheduleLifecycleChange;
                /** Change only: what happens to the current schedule's entries. */
                journalEntryMode?: ScheduleChangeJournalEntryMode;
                /** Stop only: clear the remaining balance in the same action. */
                settlement?: ScheduleStopSettlement;
            },
        ): Promise<ScheduleApplicationListItem> => {
            const response = await requestScheduleAction(
                () =>
                    LIFECYCLE_ACTION_CLIENTS[options.verb]({
                        extraHeaders: SUPPRESS_ERROR_TOAST_HEADERS,
                        params: {
                            workspaceId: workspaceId ?? "",
                            id: options.applicationId,
                        },
                        body: {
                            entityId: options.entityId,
                            change: options.change,
                            effectiveDate: options.effectiveDate,
                            journalEntryMode: options.journalEntryMode,
                            settlement: options.settlement,
                        },
                    }),
                "Failed to update the schedule",
            );
            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to update the schedule",
                    ),
                );
            }
            return response.body.application;
        },
        onSuccess: () => invalidateAfterLifecycleAction(workspaceId),
    });
}

export function useDeleteScheduleApplicationMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (options: {
            applicationId: string;
            entityId: number;
            journalEntryMode: ScheduleDeleteJournalEntryMode;
        }): Promise<{
            id: string;
            application: ScheduleApplicationListItem | null;
        }> => {
            const response = await requestScheduleAction(
                () =>
                    scheduleTemplateClient.deleteApplication({
                        extraHeaders: SUPPRESS_ERROR_TOAST_HEADERS,
                        params: {
                            workspaceId: workspaceId ?? "",
                            id: options.applicationId,
                        },
                        body: {
                            entityId: options.entityId,
                            journalEntryMode: options.journalEntryMode,
                        },
                    }),
                "Failed to delete the schedule",
            );
            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to delete the schedule",
                    ),
                );
            }
            return response.body;
        },
        onSuccess: () => invalidateAfterLifecycleAction(workspaceId),
    });
}

type TemplateStatusEndpoint =
    | typeof scheduleTemplateClient.archiveTemplate
    | typeof scheduleTemplateClient.restoreTemplate;

function useTemplateStatusMutation(
    endpoint: TemplateStatusEndpoint,
    failureMessage: string,
) {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (templateId: string): Promise<void> => {
            const response = await endpoint({
                params: { workspaceId: workspaceId ?? "", id: templateId },
                body: {},
            });
            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(response.body, failureMessage),
                );
            }
        },
        onSuccess: () => {
            if (workspaceId) {
                invalidateScheduleTemplates(workspaceId);
            }
        },
    });
}

export function useArchiveScheduleTemplateMutation() {
    return useTemplateStatusMutation(
        scheduleTemplateClient.archiveTemplate,
        "Failed to archive policy",
    );
}

export function useRestoreScheduleTemplateMutation() {
    return useTemplateStatusMutation(
        scheduleTemplateClient.restoreTemplate,
        "Failed to restore policy",
    );
}

export class ScheduleTemplateInUseError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "ScheduleTemplateInUseError";
    }
}

export function useDeleteScheduleTemplateMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (templateId: string): Promise<void> => {
            try {
                await scheduleTemplateClient.deleteTemplate({
                    extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
                    params: { workspaceId: workspaceId ?? "", id: templateId },
                });
            } catch (error) {
                const message = getApiErrorMessage(
                    error,
                    "Failed to delete policy",
                );
                throw getApiErrorStatus(error) === 409
                    ? new ScheduleTemplateInUseError(message)
                    : new Error(message);
            }
        },
        onSuccess: () => {
            if (workspaceId) {
                invalidateScheduleTemplates(workspaceId);
            }
        },
    });
}
